// Auth API Service - Backend Integration
import { apiPost, apiGet } from '../services/apiClient';
import axios from 'axios';
import toast from 'react-hot-toast';
import { mapRoleIdToFrontendRole, mapRoleCodeToFrontendRole } from '../utils/roleMapper';
import { isTokenExpired, extractUserInfo } from '../utils/jwtDecoder';
import {
  beginSessionTakeover,
  endSessionTakeover,
  clearStaleAuthTokensForTakeover,
} from '../services/sessionTakeoverGuard';
import websocketService from '../services/websocketService';

/** Backend returns this when another session exists and User A must approve (policy PENDING_USER_A). */
const PENDING_APPROVAL = 'PENDING_APPROVAL';

/** Max time to poll status (30s server decision window + small buffer). */
const MAX_SESSION_POLL_MS = 45_000;

const MSG_POLL_EXPIRED = 'Session request expired. Please login again.';
const MSG_PREAUTH_INVALID = 'Session expired. Please login again.';
const MSG_PREAUTH_CONSUMED = 'Session already used.';
const MSG_CHALLENGE_REJECTED = 'Login request was rejected.';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function redirectToSignIn() {
  window.location.assign('/signin');
}

function friendlyFlowError(err, fallback) {
  const code = err?.code;
  if (code === 'PREAUTH_INVALID' || code === 'CHALLENGE_NOT_FOUND') {
    return MSG_PREAUTH_INVALID;
  }
  if (code === 'PREAUTH_CONSUMED') {
    return MSG_PREAUTH_CONSUMED;
  }
  if (code === 'CHALLENGE_REJECTED') {
    return MSG_CHALLENGE_REJECTED;
  }
  const d = err?.response?.data;
  const m = (typeof d?.message === 'string' && d.message) || (typeof d?.code === 'string' && d.code);
  if (m && m !== 'Network Error' && !String(m).includes('API Error:')) {
    return m;
  }
  return fallback;
}

function isSessionFlowFatalCode(code) {
  return code === 'CHALLENGE_NOT_FOUND' || code === 'PREAUTH_INVALID';
}

/** Persist JWT + session fields immediately (used after session-challenge complete). */
function persistAuthSessionFromResponse(response) {
  if (!response?.token) {
    return;
  }
  sessionStorage.setItem('token', response.token);
  if (response.userId != null && response.userId !== '') {
    sessionStorage.setItem('userId', String(response.userId));
  }
  if (response.tenantId != null && response.tenantId !== '') {
    sessionStorage.setItem('tenantId', String(response.tenantId));
  }
  if (response.roles != null) {
    sessionStorage.setItem('role', response.roles);
  }
  if (response.employeeHistoryId != null) {
    sessionStorage.setItem('employeeHistoryId', String(response.employeeHistoryId));
  }
}

// ========== SESSION CONFLICT (PENDING_USER_A) ==========

export const getSessionChallengeStatus = async (challengeId, pollToken, signal) => {
  return apiGet(
    `/gateway/login/userLogin/session-challenge/${encodeURIComponent(challengeId)}/status`,
    { pollToken },
    signal ? { signal } : {}
  );
};

export const completeSessionChallenge = async ({ challengeId, pollToken, tenantId, signal }) => {
  const body = { challengeId, pollToken };
  if (tenantId !== undefined && tenantId !== null && tenantId !== '') {
    const n = typeof tenantId === 'number' ? tenantId : Number(tenantId);
    if (!Number.isNaN(n)) {
      body.tenantId = n;
    }
  }
  return apiPost('/gateway/login/userLogin/session-challenge/complete', body, signal ? { signal } : {});
};

export const allowSessionChallenge = async (challengeId) => {
  return apiPost(
    `/gateway/login/userLogin/session-challenge/${encodeURIComponent(challengeId)}/allow`,
    {}
  );
};

export const rejectSessionChallenge = async (challengeId) => {
  return apiPost(
    `/gateway/login/userLogin/session-challenge/${encodeURIComponent(challengeId)}/reject`,
    {}
  );
};

/** User B: end the other session using poll token (no JWT). */
export const allowSessionChallengeByPollToken = async (challengeId, pollToken) => {
  return apiPost(
    `/gateway/login/userLogin/session-challenge/${encodeURIComponent(challengeId)}/allow-poll`,
    { pollToken }
  );
};

/** User B: cancel this login attempt using poll token (no JWT). */
export const rejectSessionChallengeByPollToken = async (challengeId, pollToken) => {
  return apiPost(
    `/gateway/login/userLogin/session-challenge/${encodeURIComponent(challengeId)}/reject-poll`,
    { pollToken }
  );
};

/**
 * User B: poll status until APPROVED, then call complete with pollToken (never a server-only secret).
 * @param {object} initialResponse — login/OTP response with status PENDING_APPROVAL
 * @param {{ signal?: AbortSignal, waitForUserChoice?: (ctx: object) => Promise<'continue'|'cancel'> }} [options] —
 *   pass `waitForUserChoice` so the signing-in user can choose to close the other session (User B flow);
 *   omit it to keep polling until the existing session approves (User A flow).
 */
export async function resolvePendingApprovalIfNeeded(initialResponse, options = {}) {
  const { signal, waitForUserChoice } = options;

  if (!initialResponse || initialResponse.status !== PENDING_APPROVAL) {
    return initialResponse;
  }

  const challengeId = initialResponse.challengeId;
  const pollToken = initialResponse.pollToken;
  const expiresAt = initialResponse.expiresAt;

  if (!challengeId || !pollToken) {
    toast.error('Invalid pending approval response from server.');
    throw new Error('INVALID_PENDING_APPROVAL');
  }

  const isInteractive = initialResponse.clientApprovalMode === 'INTERACTIVE';
  const effectiveWaitForUserChoice =
    isInteractive &&
    (typeof waitForUserChoice === 'function'
      ? waitForUserChoice
      : (ctx) =>
        new Promise((resolve) => {
          const ok = window.confirm(
            'You already have an active session on another device or browser. Close that session and continue signing in here?'
          );
          resolve(ok ? 'continue' : 'cancel');
        }));

  if (effectiveWaitForUserChoice) {
    const decisionSeconds =
      typeof initialResponse.decisionSeconds === 'number' && Number.isFinite(initialResponse.decisionSeconds)
        ? initialResponse.decisionSeconds
        : 30;

    const abortInteractive = () => {
      if (signal?.aborted) {
        const err = new Error('LOGIN_POLL_ABORTED');
        err.name = 'AbortError';
        throw err;
      }
    };

    let choice;
    try {
      choice = await effectiveWaitForUserChoice({
        challengeId,
        pollToken,
        expiresAt,
        decisionSeconds,
      });
    } catch (e) {
      if (e?.name === 'AbortError' || signal?.aborted) {
        throw e;
      }
      throw e;
    }

    abortInteractive();

    if (choice === 'cancel') {
      try {
        await rejectSessionChallengeByPollToken(challengeId, pollToken);
      } catch (rejectErr) {
        console.warn('reject-poll:', rejectErr);
      }
      toast.error('Sign-in cancelled.');
      throw new Error('LOGIN_REJECTED');
    }

    beginSessionTakeover({ challengeId });
    websocketService.prepareForTakeover();
    clearStaleAuthTokensForTakeover();

    try {
      await allowSessionChallengeByPollToken(challengeId, pollToken);
    } catch (allowErr) {
      websocketService.abortTakeover();
      endSessionTakeover('allow_failed', { success: false });
      toast.error(
        friendlyFlowError(allowErr, 'Could not close the other session. Please try again.')
      );
      throw new Error('SESSION_CHALLENGE_ALLOW_POLL_FAILED');
    }

    abortInteractive();

    const tenantRaw = sessionStorage.getItem('tenantId');
    let tenantId;
    if (tenantRaw != null && tenantRaw !== '') {
      const n = Number(tenantRaw);
      if (!Number.isNaN(n)) {
        tenantId = n;
      }
    }

    let complete;
    try {
      complete = await completeSessionChallenge({
        challengeId,
        pollToken,
        ...(tenantId !== undefined ? { tenantId } : {}),
        ...(signal ? { signal } : {}),
      });
    } catch (completeErr) {
      websocketService.abortTakeover();
      endSessionTakeover('complete_failed', { success: false });
      if (completeErr?.name === 'AbortError' && completeErr?.isUserAbort) {
        throw completeErr;
      }
      const code = completeErr?.code;
      if (code === 'PREAUTH_INVALID' || code === 'CHALLENGE_NOT_FOUND') {
        toast.error(MSG_PREAUTH_INVALID);
        redirectToSignIn();
        throw new Error('SESSION_FLOW_TERMINATED');
      }
      if (code === 'PREAUTH_CONSUMED') {
        toast.error(MSG_PREAUTH_CONSUMED);
        redirectToSignIn();
        throw new Error('SESSION_FLOW_TERMINATED');
      }
      if (code === 'CHALLENGE_REJECTED') {
        toast.error(MSG_CHALLENGE_REJECTED);
        throw new Error('LOGIN_REJECTED');
      }
      toast.error(
        friendlyFlowError(
          completeErr,
          'Could not complete sign-in after closing the other session. Please try again.'
        )
      );
      throw new Error('SESSION_CHALLENGE_COMPLETE_FAILED');
    }

    persistAuthSessionFromResponse(complete);
    websocketService.completeTakeover();
    endSessionTakeover('completed', { success: true });
    return complete;
  }

  const loadingId = toast.loading('Waiting for approval from your other session…');
  const startMs = Date.now();
  const expiresMs = expiresAt ? new Date(expiresAt).getTime() : null;

  // If server clock is wrong, expiresAt can be *in the past*. Old logic did Math.min(now+45s, past)
  // → deadline in the past → while-loop never ran → instant LOGIN_POLL_TIMEOUT with no real wait.
  let deadlineMs;
  if (expiresMs != null && !Number.isNaN(expiresMs) && expiresMs > startMs) {
    deadlineMs = Math.min(startMs + MAX_SESSION_POLL_MS, expiresMs);
  } else {
    deadlineMs = startMs + MAX_SESSION_POLL_MS;
  }

  const abortIfNeeded = () => {
    if (signal?.aborted) {
      toast.dismiss(loadingId);
      toast.error('Sign-in was cancelled.');
      const err = new Error('LOGIN_POLL_ABORTED');
      err.name = 'AbortError';
      throw err;
    }
  };

  const handleStatusOrPollError = (err) => {
    if (err?.name === 'AbortError' && (err?.isUserAbort || signal?.aborted)) {
      throw err;
    }
    const code = err?.code;
    if (isSessionFlowFatalCode(code)) {
      toast.dismiss(loadingId);
      toast.error(MSG_PREAUTH_INVALID);
      redirectToSignIn();
      const terminated = new Error('SESSION_FLOW_TERMINATED');
      terminated.causeCode = code;
      throw terminated;
    }
    toast.dismiss(loadingId);
    toast.error(
      friendlyFlowError(err, 'Something went wrong while checking approval status. Please try again.')
    );
    throw new Error('LOGIN_PENDING_FLOW_FAILED');
  };

  try {
    while (Date.now() < deadlineMs) {
      abortIfNeeded();
      let statusRes;
      try {
        statusRes = await getSessionChallengeStatus(challengeId, pollToken, signal);
      } catch (err) {
        handleStatusOrPollError(err);
      }
      const st = statusRes?.status;
      if (st === 'REJECTED' || st === 'TIMEOUT') {
        toast.dismiss(loadingId);
        toast.error(MSG_CHALLENGE_REJECTED);
        throw new Error('LOGIN_REJECTED');
      }
      if (st === 'APPROVED') {
        break;
      }
      await sleep(1500);
    }

    abortIfNeeded();

    let finalCheck;
    try {
      finalCheck = await getSessionChallengeStatus(challengeId, pollToken, signal);
    } catch (err) {
      handleStatusOrPollError(err);
    }
    const st = finalCheck?.status;
    if (st === 'REJECTED' || st === 'TIMEOUT') {
      toast.dismiss(loadingId);
      toast.error(MSG_CHALLENGE_REJECTED);
      throw new Error('LOGIN_REJECTED');
    }
    if (st !== 'APPROVED') {
      toast.dismiss(loadingId);
      toast.error(MSG_POLL_EXPIRED);
      redirectToSignIn();
      throw new Error('LOGIN_POLL_TIMEOUT');
    }

    const tenantRaw = sessionStorage.getItem('tenantId');
    let tenantId;
    if (tenantRaw != null && tenantRaw !== '') {
      const n = Number(tenantRaw);
      if (!Number.isNaN(n)) {
        tenantId = n;
      }
    }

    let complete;
    try {
      complete = await completeSessionChallenge({
        challengeId,
        pollToken,
        ...(tenantId !== undefined ? { tenantId } : {}),
        ...(signal ? { signal } : {}),
      });
    } catch (completeErr) {
      if (completeErr?.name === 'AbortError' && completeErr?.isUserAbort) {
        throw completeErr;
      }
      const code = completeErr?.code;
      toast.dismiss(loadingId);
      if (code === 'PREAUTH_INVALID' || code === 'CHALLENGE_NOT_FOUND') {
        toast.error(MSG_PREAUTH_INVALID);
        redirectToSignIn();
        throw new Error('SESSION_FLOW_TERMINATED');
      }
      if (code === 'PREAUTH_CONSUMED') {
        toast.error(MSG_PREAUTH_CONSUMED);
        redirectToSignIn();
        throw new Error('SESSION_FLOW_TERMINATED');
      }
      if (code === 'CHALLENGE_REJECTED') {
        toast.error(MSG_CHALLENGE_REJECTED);
        throw new Error('LOGIN_REJECTED');
      }
      toast.error(
        friendlyFlowError(
          completeErr,
          'Could not complete sign-in after approval. Please try again.'
        )
      );
      throw new Error('SESSION_CHALLENGE_COMPLETE_FAILED');
    }

    toast.dismiss(loadingId);
    return complete;
  } catch (e) {
    toast.dismiss(loadingId);
    if (e?.name === 'AbortError' || e?.message === 'LOGIN_POLL_ABORTED') {
      throw e;
    }
    if (
      e?.message === 'SESSION_FLOW_TERMINATED' ||
      e?.message === 'LOGIN_REJECTED' ||
      e?.message === 'LOGIN_POLL_TIMEOUT' ||
      e?.message === 'INVALID_PENDING_APPROVAL' ||
      e?.message === 'SESSION_CHALLENGE_COMPLETE_FAILED' ||
      e?.message === 'SESSION_CHALLENGE_ALLOW_POLL_FAILED' ||
      e?.message === 'LOGIN_PENDING_FLOW_FAILED'
    ) {
      throw e;
    }
    toast.error(
      friendlyFlowError(e, 'Something went wrong while waiting for approval. Please try again.')
    );
    throw new Error('LOGIN_PENDING_FLOW_FAILED');
  }
}

// CAPTCHA API (direct to Login Service, not via Gateway)
// Note: CAPTCHA endpoints are accessed directly from the service, not through the API Gateway
// Local: http://localhost:8081/captcha
// Production: http://192.168.20.187:9091/captcha
const CAPTCHA_BASE_URL =
  process.env.REACT_APP_CAPTCHA_BASE_URL || 'http://localhost:8080/gateway';
const CAPTCHA_API = axios.create({
  baseURL: `${CAPTCHA_BASE_URL}/captcha`,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ========== LOGIN FLOW ==========

export const checkUserStatus = async (email) => {
  // tenantId removed - will be extracted from user record in backend
  return await apiPost('/gateway/login/userLogin/check-user-status', { email });
};

export const requestOtp = async (email) => {
  // tenantId removed - will be extracted from user record in backend
  return await apiPost('/gateway/login/userLogin/request-otp', { email });
};

export const verifyOtp = async (email, otpCode, sessionConflictOptions = {}) => {
  let response = await apiPost('/gateway/login/userLogin/verify-otp', { email, otpCode });

  if (response?.status === PENDING_APPROVAL) {
    response = await resolvePendingApprovalIfNeeded(response, sessionConflictOptions);
  }

  // Same session + profile load as password login (`login`)
  if (response?.token) {
    sessionStorage.setItem('token', response.token);
    sessionStorage.setItem('tenantId', response.tenantId);

    if (response.userId != null && response.userId !== '') {
      sessionStorage.setItem('userId', String(response.userId));
    }
    if (response.roles != null) {
      sessionStorage.setItem('role', response.roles);
    }
    if (response.employeeHistoryId != null) {
      sessionStorage.setItem('employeeHistoryId', String(response.employeeHistoryId));
    }

    let userIdForProfile = response.userId;
    if (userIdForProfile == null || userIdForProfile === '') {
      const fromJwt = extractUserInfo(response.token);
      userIdForProfile = fromJwt?.userId;
      if (userIdForProfile != null && userIdForProfile !== '') {
        sessionStorage.setItem('userId', String(userIdForProfile));
      }
    }

    if (userIdForProfile != null && userIdForProfile !== '') {
      getUserLoginDetails(userIdForProfile).catch((err) => {
        console.error('Error fetching user login details after OTP:', err);
      });
    }
  }

  return response;
};

export const login = async (
  loginName,
  password,
  captcha = null,
  captchaId = null,
  sessionConflictOptions = {}
) => {
  const requestBody = {
    loginName,
    password,
    // TENANT_ID removed - will be extracted from user record in backend
  };

  // Add CAPTCHA if provided
  if (captcha && captchaId) {
    requestBody.captcha = captcha;
    requestBody.captchaId = captchaId;
  }

  let response = await apiPost('/gateway/login/userLogin/login', requestBody);

  if (response?.status === PENDING_APPROVAL) {
    response = await resolvePendingApprovalIfNeeded(response, sessionConflictOptions);
  }

  // Store token from response (use sessionStorage for tab-specific authentication)
  if (response?.token) {
    sessionStorage.setItem('token', response.token);
    sessionStorage.setItem('userId', response.userId);
    //console.log('test', response.userId);
    // tenantId comes from response (extracted from user record in backend)
    sessionStorage.setItem('tenantId', response.tenantId);
    sessionStorage.setItem('role', response.roles);

    sessionStorage.setItem('employeeHistoryId', response.employeeHistoryId);
    getUserLoginDetails(response.userId);


  }

  return response;
};

export const googleOAuthLogin = async (idToken, tenantId, clientId, sessionConflictOptions = {}) => {
  let response = await apiPost('/gateway/login/userLogin/oauth/google', {
    idToken,
    tenantId,
    clientId,
  });

  if (response?.status === PENDING_APPROVAL) {
    response = await resolvePendingApprovalIfNeeded(response, sessionConflictOptions);
  }

  if (response?.token) {
    sessionStorage.setItem('token', response.token);
    sessionStorage.setItem('userId', response.userId);
    sessionStorage.setItem('tenantId', response.tenantId);
    sessionStorage.setItem('role', response.roles);
    sessionStorage.setItem('employeeHistoryId', response.employeeHistoryId);
    getUserLoginDetails(response.userId);
  }
  return response;
};
export const getUserLoginDetails = async (userId) => {
  try {
    const response = await apiGet(
      `/gateway/registration/api/signup/organization/users/${userId}`
    );

    if (response?.data) {
      const userData = response.data;

      // ✅ Store required fields in both sessionStorage and localStorage
      const userName = userData.fullName || '';
      const userDetailsJson = JSON.stringify(userData);

      // Store in sessionStorage (tab-specific)
      sessionStorage.setItem('userName', userName);
      sessionStorage.setItem('userDetails', userDetailsJson);
      sessionStorage.setItem('roleId', userData.roleId);

      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('hd:user-details-updated', { detail: userData })
        );
      }

      // Also store in localStorage (for backward compatibility and cross-tab access)
      // localStorage.setItem('userName', userName);
      // localStorage.setItem('userDetails', userDetailsJson);
      // localStorage.setItem('roleId', userData.roleId);

      // Optional fields (store only if needed)
      // if (userData.firstName) {
      //   sessionStorage.setItem('firstName', userData.firstName);
      //   localStorage.setItem('firstName', userData.firstName);
      // }
      // if (userData.lastName) {
      //   sessionStorage.setItem('lastName', userData.lastName);
      //   localStorage.setItem('lastName', userData.lastName);
      // }
    }

    return response;
  } catch (error) {
    console.error('Error fetching user login details:', error);
    throw error;
  }
};


export const changePasswordFirstTime = async (
  email,
  currentPassword,
  newPassword,
  confirmPassword,
  sessionConflictOptions = {}
) => {
  // tenantId removed - will be extracted from user record in backend
  let response = await apiPost('/gateway/login/userLogin/change-password-first-time', {
    email,
    currentPassword,
    newPassword,
    confirmPassword,
  });

  if (response?.status === PENDING_APPROVAL) {
    response = await resolvePendingApprovalIfNeeded(response, sessionConflictOptions);
  }

  // Store token from response (use sessionStorage for tab-specific authentication)
  if (response?.token) {
    sessionStorage.setItem('token', response.token);
    // tenantId comes from response (extracted from user record in backend)
    sessionStorage.setItem('tenantId', response.tenantId);
    sessionStorage.setItem('userId', response.userId);
    sessionStorage.setItem('role', response.roles);
    sessionStorage.setItem('employeeHistoryId', response.employeeHistoryId);
  }

  return response;
};

// ========== PASSWORD RESET FLOW ==========

export const forgotPassword = async (email) => {
  return await apiPost('/gateway/login/userLogin/forgot-password', { email });
};

export const resetPassword = async (email, token, newPassword, confirmPassword) => {
  return await apiPost('/gateway/login/userLogin/reset-password', {
    email,
    token,
    newPassword,
    confirmPassword,
  });
};

export const changePassword = async (currentPassword, newPassword, confirmPassword) => {
  return await apiPost('/gateway/login/userLogin/change-password', {
    currentPassword,
    newPassword,
    confirmPassword,
  });
};

// ========== CAPTCHA ==========

export const getCaptchaImage = async () => {
  const response = await CAPTCHA_API.get('/captchaImg');
  return response.data;
};

export const reloadCaptchaImage = async (previousCaptchaId) => {
  const response = await CAPTCHA_API.get(`/reloadCaptchaImg/${previousCaptchaId}`);
  return response.data;
};

export const validateCaptcha = async (captchaId, captchaAnswer) => {
  const response = await CAPTCHA_API.get(`/validateCaptcha/${captchaId}`, {
    params: { captchaAnswer },
  });
  return response.data;
};

// ========== SESSION MANAGEMENT ==========

export const logout = async () => {
  try {
    // Get userId and employeeHistoryId from sessionStorage before clearing
    const userId = sessionStorage.getItem('userId');
    const employeeHistoryId = sessionStorage.getItem('employeeHistoryId');

    // Call logout API if we have both required values
    if (userId && employeeHistoryId) {
      try {
        await apiPost('/gateway/login/userLogin/logout', {
          userId: parseInt(userId, 10),
          employeeHistoryId: parseInt(employeeHistoryId, 10)
        });
      } catch (error) {
        // Log error but don't block logout - user should still be logged out even if API fails
        console.error('Logout API call failed:', error);
        // Continue with logout process even if API call fails
      }
    } else {
      console.warn('Logout called but userId or employeeHistoryId not found in sessionStorage');
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Continue with logout process even if there's an error
  } finally {
    // Always clear storage regardless of API call success/failure
    sessionStorage.clear();
    localStorage.clear();
  }
};

export const getStoredUserTeamName = () => {
  try {
    const raw = sessionStorage.getItem('userDetails');
    if (!raw) return '';
    const details = JSON.parse(raw);
    const name = details?.teamName || details?.team_name || '';
    return typeof name === 'string' ? name.trim() : '';
  } catch {
    return '';
  }
};

/** Configured role name from login user profile (not role code). */
export const getStoredUserRoleName = () => {
  try {
    const raw = sessionStorage.getItem('userDetails');
    if (!raw) return '';
    const details = JSON.parse(raw);
    const name = details?.roleName || details?.role_name || '';
    return typeof name === 'string' ? name.trim() : '';
  } catch {
    return '';
  }
};

/** Role code for authorization/UI logic (not header display). */
export const getStoredUserRoleCode = () => {
  try {
    const raw = sessionStorage.getItem('userDetails');
    if (raw) {
      const details = JSON.parse(raw);
      const code = details?.roleCode || details?.role_code || details?.role || '';
      if (typeof code === 'string' && code.trim()) {
        return code.trim();
      }
    }
  } catch {
    /* ignore */
  }
  const user = getCurrentUser();
  return user?.roleCode || user?.role || '';
};

export const getCurrentUser = () => {
  // Use sessionStorage only
  const token = sessionStorage.getItem('token');
  if (!token) return null;

  try {
    const userInfo = extractUserInfo(token);
    if (!userInfo || !userInfo.userId) {
      return null;
    }

    // Map role to frontend role string - prefer role_code over roleId for multi-tenant support
    // Role codes are consistent across tenants (e.g., "ADMIN", "AGENT"), while role IDs are tenant-specific
    let frontendRole;
    // console.log('userInfo', userInfo);
    if (userInfo.roleCode) {
      // Use role_code if available (more reliable for multi-tenant)
      frontendRole = mapRoleCodeToFrontendRole(userInfo.roleCode);
    } else {
      // Fallback to roleId mapping (for backward compatibility)
      frontendRole = mapRoleIdToFrontendRole(userInfo.roleId);
    }

    return {
      userId: userInfo.userId,
      email: userInfo.email || userInfo.username,
      username: userInfo.username,
      tenantId: userInfo.tenantId,
      roleId: userInfo.roleId,
      roleCode: userInfo.roleCode, // Role code from JWT (e.g., "ADMIN", "AGENT")
      role: frontendRole, // Frontend-compatible role string
      roles: userInfo.roles || [],
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const isAuthenticated = () => {
  // Use sessionStorage only
  const token = sessionStorage.getItem('token');
  if (!token) return false;

  // Check token expiration
  if (isTokenExpired(token)) {
    import('../services/logoutService').then(({ performLogout, isLogoutInProgress }) => {
      if (!isLogoutInProgress()) {
        performLogout({ reason: 'token-expired' });
      }
    });
    return false;
  }

  return true;
};

export const getToken = () => {
  // Use sessionStorage only
  return sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
};

// Default export for backward compatibility
const authService = {
  checkUserStatus,
  requestOtp,
  verifyOtp,
  login,
  googleOAuthLogin,
  getUserLoginDetails,
  changePasswordFirstTime,
  forgotPassword,
  resetPassword,
  changePassword,
  getCaptchaImage,
  reloadCaptchaImage,
  validateCaptcha,
  logout,
  getCurrentUser,
  getStoredUserTeamName,
  getStoredUserRoleName,
  getStoredUserRoleCode,
  isAuthenticated,
  getToken,
  resolvePendingApprovalIfNeeded,
  getSessionChallengeStatus,
  completeSessionChallenge,
  allowSessionChallenge,
  rejectSessionChallenge,
  allowSessionChallengeByPollToken,
  rejectSessionChallengeByPollToken,
};

export default authService;
